import React, { useState, useMemo } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { useAccessResolver } from '@/contexts/AccessResolverContext';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FileText,
  Shield,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { cn } from '@/lib/utils';

// Common audit log actions
const AUDIT_ACTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'user.invited', label: 'User Invited' },
  { value: 'user.accepted', label: 'User Accepted' },
  { value: 'user.removed', label: 'User Removed' },
  { value: 'user.role_changed', label: 'Role Changed' },
  { value: 'user.status_changed', label: 'Status Changed' },
  { value: 'module.enabled', label: 'Module Enabled' },
  { value: 'module.disabled', label: 'Module Disabled' },
  { value: 'workspace.updated', label: 'Workspace Updated' },
  { value: 'workspace.settings_changed', label: 'Settings Changed' },
  { value: 'billing.plan_changed', label: 'Plan Changed' },
  { value: 'billing.payment_updated', label: 'Payment Updated' },
];

const ITEMS_PER_PAGE = 50;

export default function AuditLogs() {
  const { currentWorkspace } = useWorkspace();
  const { user: currentUser } = useAuth();
  const { isAdmin } = useAccessResolver();

  // Filter state
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const offset = currentPage * ITEMS_PER_PAGE;

  // Expanded metadata rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Build filters
  const filters = useMemo(() => {
    const filterParams: any = {
      limit: ITEMS_PER_PAGE,
      offset,
    };

    if (selectedAction) {
      filterParams.action = selectedAction;
    }

    if (dateRange.from) {
      filterParams.start_date = startOfDay(dateRange.from).toISOString();
    }

    if (dateRange.to) {
      filterParams.end_date = endOfDay(dateRange.to).toISOString();
    }

    return filterParams;
  }, [selectedAction, dateRange, offset]);

  const {
    logs,
    total,
    isLoading,
    error,
  } = useAuditLogs(currentWorkspace?.id, filters);

  // Check if user can access this page (owner or admin)
  const canAccess = currentUser?.role === 'super_admin' ||
                   currentUser?.role === 'owner' ||
                   isAdmin();

  // If user doesn't have access, show access denied
  if (!canAccess) {
    return (
      <div className="space-y-6 p-6">
        <Alert variant="destructive">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            You do not have permission to access audit logs. Only workspace owners and administrators can view audit logs.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show loading if workspace is not available yet
  if (!currentWorkspace?.id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  const toggleMetadata = (logId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const clearFilters = () => {
    setSelectedAction('');
    setDateRange({
      from: subDays(new Date(), 30),
      to: new Date(),
    });
    setCurrentPage(0);
  };

  const hasActiveFilters = selectedAction !== '' || 
                           dateRange.from?.getTime() !== subDays(new Date(), 30).getTime() ||
                           dateRange.to?.getTime() !== new Date().getTime();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load audit logs. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Audit Logs</h1>
          <p className="text-muted-foreground">
            Track all actions and changes in your workspace
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            {/* Action Filter */}
            <div className="space-y-2 min-w-[200px]">
              <label className="text-sm font-medium">Action</label>
              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_ACTIONS.map((action) => (
                    <SelectItem key={action.value} value={action.value}>
                      {action.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Picker */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date Range</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-[300px] justify-start text-left font-normal',
                      !dateRange.from && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'LLL dd, y')} -{' '}
                          {format(dateRange.to, 'LLL dd, y')}
                        </>
                      ) : (
                        format(dateRange.from, 'LLL dd, y')
                      )
                    ) : (
                      <span>Pick a date range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{
                      from: dateRange.from,
                      to: dateRange.to,
                    }}
                    onSelect={(range) => {
                      setDateRange({
                        from: range?.from,
                        to: range?.to,
                      });
                      setCurrentPage(0); // Reset to first page when filter changes
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Audit Logs
              </CardTitle>
              <CardDescription>
                {total} {total === 1 ? 'log' : 'logs'} found
                {hasActiveFilters && ' (filtered)'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-2">No audit logs found</p>
              <p className="text-sm">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Audit logs will appear here as actions are performed'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead className="w-[100px]">Metadata</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => {
                      const isExpanded = expandedRows.has(log.id);
                      return (
                        <React.Fragment key={log.id}>
                          <TableRow>
                            <TableCell className="font-mono text-sm">
                              {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">
                                  {log.actor.name || log.actor.email}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {log.actor.email}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{log.action}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">{log.target.type}</div>
                                {log.target.name && (
                                  <div className="text-xs text-muted-foreground">
                                    {log.target.name}
                                  </div>
                                )}
                                {log.target.id && (
                                  <div className="text-xs text-muted-foreground font-mono">
                                    {log.target.id.substring(0, 8)}...
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {log.metadata && Object.keys(log.metadata).length > 0 ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleMetadata(log.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                          {isExpanded && log.metadata && (
                            <TableRow>
                              <TableCell colSpan={5} className="bg-muted/50">
                                <div className="p-4">
                                  <pre className="text-xs font-mono overflow-x-auto">
                                    {JSON.stringify(log.metadata, null, 2)}
                                  </pre>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {offset + 1} to {Math.min(offset + ITEMS_PER_PAGE, total)} of {total} logs
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                      disabled={!hasPrevPage}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <div className="text-sm text-muted-foreground">
                      Page {currentPage + 1} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      disabled={!hasNextPage}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
